/**
 * OAuth Popup Utilities
 *
 * Provides a consistent way to handle OAuth popup flows with:
 * - Centered popup positioning
 * - BroadcastChannel communication (works with COOP restrictions)
 * - Automatic cleanup with timeout
 *
 * Note: Uses BroadcastChannel instead of postMessage because COOP
 * (Cross-Origin-Opener-Policy) breaks window.opener when popup
 * navigates to external OAuth provider (e.g., accounts.google.com).
 */

/** Channel name for OAuth popup communication */
export const OAUTH_CHANNEL_NAME = 'oauth_popup_channel';

export interface OAuthPopupOptions {
  /** URL to open in popup */
  url: string;
  /** Popup window name */
  name: string;
  /** Popup width (default: 600) */
  width?: number;
  /** Popup height (default: 700) */
  height?: number;
  /** Timeout in ms before cleanup (default: 5 minutes) */
  timeout?: number;
  /** Callback when OAuth completes */
  onMessage?: (data: Record<string, unknown>) => void;
  /** Ignore completion messages emitted by unrelated simultaneous OAuth flows. */
  acceptMessage?: (data: Record<string, unknown>) => boolean;
  /** Callback when timeout expires without OAuth completion */
  onClose?: () => void;
  /** Called only at expiry, unlike onClose which can fire early under COOP. */
  onTimeout?: () => void;
  /** Error key to check in message data */
  errorKey?: string;
  /** Callback when error is received */
  onError?: (error: string) => void;
}

/**
 * Opens an OAuth popup with proper handling for COOP restrictions.
 *
 * Uses BroadcastChannel for communication because COOP breaks window.opener
 * when popup navigates to external OAuth provider (e.g., accounts.google.com).
 */
export function openOAuthPopup(options: OAuthPopupOptions): void {
  const {
    url,
    name,
    width = 600,
    height = 700,
    timeout = 5 * 60 * 1000,
    onMessage,
    acceptMessage,
    onClose,
    onTimeout,
    errorKey,
    onError,
  } = options;

  // Center the popup
  const left = window.screenX + (window.outerWidth - width) / 2;
  const top = window.screenY + (window.outerHeight - height) / 2;

  const popup = window.open(
    url,
    name,
    `width=${width},height=${height},left=${left},top=${top},popup=yes`
  );

  if (!popup) {
    onError?.('Failed to open popup window. Please allow popups for this site.');
    return;
  }

  let timeoutTimer: ReturnType<typeof setTimeout> | null = null;
  let pollTimer: ReturnType<typeof setInterval> | null = null;
  let channel: BroadcastChannel | null = null;
  let messageReceived = false;

  const cleanup = () => {
    if (timeoutTimer) {
      clearTimeout(timeoutTimer);
      timeoutTimer = null;
    }
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
    if (channel) {
      channel.close();
      channel = null;
    }
  };

  // Use BroadcastChannel for cross-window communication
  // This works even when COOP breaks window.opener
  channel = new BroadcastChannel(OAUTH_CHANNEL_NAME);
  channel.onmessage = (event: MessageEvent) => {
    if (event.data?.type !== 'oauth_complete') return;
    if (acceptMessage && !acceptMessage(event.data)) return;

    messageReceived = true;
    cleanup();
    onMessage?.(event.data);

    // Check for error in response
    if (errorKey && event.data[errorKey]) {
      onError?.(String(event.data[errorKey]));
    }
  };

  // Poll for popup closure — triggers onClose but keeps BroadcastChannel open.
  // IMPORTANT: COOP (Cross-Origin-Opener-Policy) on the OAuth provider (e.g., Shopify)
  // can cause popup.closed to return true as soon as the popup navigates cross-origin,
  // even though the popup is still open. We must NOT close the BroadcastChannel here,
  // or the real oauth_complete message will be lost when the popup returns to our origin.
  pollTimer = setInterval(() => {
    if (popup.closed && !messageReceived) {
      // Stop polling only — BroadcastChannel stays open for the real message
      if (pollTimer) {
        clearInterval(pollTimer);
        pollTimer = null;
      }
      onClose?.();
    }
  }, 500);

  // Full cleanup after timeout (if OAuth wasn't completed)
  timeoutTimer = setTimeout(() => {
    cleanup();
    if (!messageReceived) {
      onTimeout?.();
      onClose?.();
    }
  }, timeout);
}
